import { Alert, Severity, CloudType } from '../../types/alerts';
import {
  AlertTriangle,
  Shield,
  Cloud,
  GitBranch,
  Activity,
  Users,
  Server,
  Clock,
  Loader2,
  Save,
  Check,
  Edit2,
  X
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../ui/common-ui/dialog';
import { Badge } from '../../../../ui/common-ui/badge';
import { Button } from '../../../../ui/common-ui/button';
import { Separator } from '../../../../ui/common-ui/separator';
import { Checkbox } from '../../../../ui/common-ui/checkbox';
import { Input } from '../../../../ui/common-ui/input';
import { Textarea } from '../../../../ui/common-ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../ui/common-ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../ui/common-ui/select';
import { toast } from 'sonner';
import { TimeWindowSelector } from './TimeWindowSelector';
import {
  fetchAlertSelectableItems,
  updateAlert,
  SelectableItemsResponse
} from '../../services/api';

interface AlertDetailsEditProps {
  alert: Alert | null;
  open: boolean;
  onClose: () => void;
  tenantName: string;
  cloudId: string;
  selectedCloud: CloudType;
  isCreateMode?: boolean;
  onAlertUpdate?: (alert: Alert) => void;
  onSaveComplete?: () => void;
  onNavigateToPolicy?: (alert: Alert) => void;
}

const getSeverityConfig = (severity: Severity) => {
  const configs = {
    Critical: { className: 'bg-red-500/10 border-red-500/30 text-red-600', icon: AlertTriangle, label: 'Critical' },
    High: { className: 'bg-orange-500/10 border-orange-500/30 text-orange-600', icon: AlertTriangle, label: 'High' },
    Medium: { className: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600', icon: AlertTriangle, label: 'Medium' },
    Low: { className: 'bg-green-500/10 border-green-500/30 text-green-600', icon: Shield, label: 'Low' },
  };
  return configs[severity] || configs.Low;
};

export const AlertDetailsEdit = ({ 
  alert, 
  open, 
  onClose, 
  tenantName, 
  cloudId, 
  selectedCloud,
  isCreateMode = false,
  onAlertUpdate,
  onSaveComplete,
  onNavigateToPolicy
}: AlertDetailsEditProps) => {
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectableItems, setSelectableItems] = useState<SelectableItemsResponse | null>(null);
  
  // Editable fields
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [selectedTimeWindows, setSelectedTimeWindows] = useState<string[]>([]);

  // Search queries (hide scrollbars but keep scroll usable)
  const [userSearch, setUserSearch] = useState('');
  const [principalSearch, setPrincipalSearch] = useState('');
  const [resourceSearch, setResourceSearch] = useState('');

  // Create mode editable fields
  const [editTitle, setEditTitle] = useState('');
  const [editSeverity, setEditSeverity] = useState<Severity>('Medium');
  const [editDescription, setEditDescription] = useState('');
  const [editWhy, setEditWhy] = useState('');
  const [editHow, setEditHow] = useState('');
  
  // Initial state for comparison (to detect actual changes)
  const [initialUsers, setInitialUsers] = useState<string[]>([]);
  const [initialResources, setInitialResources] = useState<string[]>([]);
  const [initialTimeWindows, setInitialTimeWindows] = useState<string[]>([]);
  const [initialTitle, setInitialTitle] = useState('');
  const [initialSeverity, setInitialSeverity] = useState<Severity>('Medium');
  const [initialDescription, setInitialDescription] = useState('');
  const [initialWhy, setInitialWhy] = useState('');
  const [initialHow, setInitialHow] = useState('');

  // Helper to compare arrays
  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  // Compute hasChanges dynamically by comparing current state with initial state
  const hasChanges = isCreateMode
    ? (
        editTitle !== initialTitle ||
        editSeverity !== initialSeverity ||
        editDescription !== initialDescription ||
        editWhy !== initialWhy ||
        editHow !== initialHow ||
        !arraysEqual(selectedUsers, initialUsers) ||
        !arraysEqual(selectedResources, initialResources) ||
        !arraysEqual(selectedTimeWindows, initialTimeWindows)
      )
    : (
        !arraysEqual(selectedUsers, initialUsers) ||
        !arraysEqual(selectedResources, initialResources) ||
        !arraysEqual(selectedTimeWindows, initialTimeWindows)
      );

  // Fetch selectable items when dialog opens
  useEffect(() => {
    if (open && alert && tenantName && cloudId) {
      fetchSelectableItems();
      // Initialize from alert data
      const users = alert.users || [];
      const resources = alert.resource || [];
      const timeWindows = alert.timeWindow || [];
      
      setSelectedUsers(users);
      setSelectedResources(resources);
      setSelectedTimeWindows(timeWindows);
      
      // Store initial values for comparison
      setInitialUsers(users);
      setInitialResources(resources);
      setInitialTimeWindows(timeWindows);
      
      // Initialize create mode fields
      if (isCreateMode) {
        setEditTitle(alert.title);
        setEditSeverity(alert.severity);
        setEditDescription(alert.info.what || '');
        setEditWhy(alert.info.why || '');
        setEditHow(alert.info.how || '');
        
        // Store initial values for create mode
        setInitialTitle(alert.title);
        setInitialSeverity(alert.severity);
        setInitialDescription(alert.info.what || '');
        setInitialWhy(alert.info.why || '');
        setInitialHow(alert.info.how || '');
      }
    }
  }, [open, alert, tenantName, cloudId, isCreateMode]);

  const fetchSelectableItems = async () => {
    setIsLoadingItems(true);
    try {
      const actualCloudId = alert?.cloudAccountId || cloudId;
      const actualTenantName = alert?.tenantName || tenantName;
      const items = await fetchAlertSelectableItems(actualTenantName, actualCloudId, selectedCloud);
      setSelectableItems(items);
    } catch (error) {
      console.error('Failed to fetch selectable items:', error);
      toast.error('Failed to load selectable items');
    } finally {
      setIsLoadingItems(false);
    }
  };

  if (!alert) return null;

  const severityConfig = getSeverityConfig(isCreateMode ? editSeverity : alert.severity);
  const SeverityIcon = severityConfig.icon;


  const handleUserToggle = (user: string) => {
    setSelectedUsers(prev => 
      prev.includes(user) 
        ? prev.filter(u => u !== user) 
        : [...prev, user]
    );
  };

  const handleResourceToggle = (resource: string) => {
    setSelectedResources(prev => 
      prev.includes(resource) 
        ? prev.filter(r => r !== resource) 
        : [...prev, resource]
    );
  };

  const handleTimeWindowsChange = (windows: string[]) => {
    setSelectedTimeWindows(windows);
  };

  const handleSave = async () => {
    if (!alert.alertId) {
      toast.error('Cannot update alert: missing alert ID');
      return;
    }

    setIsSaving(true);
    try {
      await updateAlert({
        alertId: alert.alertId,
        users: selectedUsers,
        resource: selectedResources,
        timeWindow: selectedTimeWindows,
        cloudType: selectedCloud,
        tenantName,
        cloudId,
      });
      
      toast.success('Alert updated successfully');
      onSaveComplete?.();
      onClose();
    } catch (error) {
      console.error('Failed to update alert:', error);
      toast.error('Failed to update alert');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateModeUpdate = () => {
    if (!onAlertUpdate) return;
    
    const updatedAlert: Alert = {
      ...alert,
      title: editTitle,
      severity: editSeverity,
      info: {
        why: editWhy,
        what: editDescription,
        how: editHow,
      },
      users: selectedUsers,
      resource: selectedResources,
      timeWindow: selectedTimeWindows,
    };
    
    onAlertUpdate(updatedAlert);
    
    // Update initial values to reflect the saved state
    setInitialTitle(editTitle);
    setInitialSeverity(editSeverity);
    setInitialDescription(editDescription);
    setInitialWhy(editWhy);
    setInitialHow(editHow);
    setInitialUsers([...selectedUsers]);
    setInitialResources([...selectedResources]);
    setInitialTimeWindows([...selectedTimeWindows]);
    
    toast.success('Alert updated');
  };

  const getResourceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'virtual-machine': 'Virtual Machines',
      'database': 'Databases',
      'storage-bucket': 'Storage Buckets',
      'ec2': 'EC2 Instances',
      'rds': 'RDS Databases',
      's3': 'S3 Buckets',
    };
    return labels[type] || type;
  };


  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <DialogHeader className="space-y-3 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                {/* Type Badge */}
                <Badge variant="default" className="w-fit bg-primary">
                  {alert.type === 'sequential' ? (
                    <>
                      <GitBranch className="h-3 w-3 mr-1.5" />
                      Sequential
                    </>
                  ) : alert.type === 'rule' ? (
                    <>
                      <Activity className="h-3 w-3 mr-1.5" />
                      Rule
                    </>
                  ) : (
                    <>
                      <Shield className="h-3 w-3 mr-1.5" />
                      Behavioral
                    </>
                  )}
                </Badge>
                {/* Severity Badge */}
                {isCreateMode ? (
                  <Select value={editSeverity} onValueChange={(v) => setEditSeverity(v as Severity)}>
                    <SelectTrigger className="w-[120px] h-7">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge
                    variant="outline"
                    className={`${severityConfig.className} w-fit`}
                  >
                    <SeverityIcon className="h-3 w-3 mr-1.5" />
                    {severityConfig.label}
                  </Badge>
                )}
              </div>
              {isCreateMode ? (
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="text-2xl font-bold h-12 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder="Alert title..."
                />
              ) : (
                <DialogTitle className="text-2xl font-bold leading-tight">
                  {alert.title}
                </DialogTitle>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto scrollbar-hide space-y-6 py-4 pr-2">
          {/* Quick Info */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Cloud Provider</p>
              <div className="flex items-center gap-2">
                <Cloud className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">{alert.cloud}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-1">Alert Type</p>
              <div className="flex items-center gap-2 justify-end">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium capitalize">{alert.type}</p>
              </div>
            </div>
          </div>

          {/* Detection Steps - moved above selections */}
          {alert.steps && Object.keys(alert.steps).length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {alert.type === 'sequential' ? 'Attack Timeline' : 'Detection Step'}
                </h3>
                <div className="relative space-y-3">
                  {Object.keys(alert.steps).length > 1 && (
                    <div className="absolute left-[9px] top-8 bottom-5 w-0.5 bg-border" />
                  )}
                  {Object.entries(alert.steps).map(([step, action], index) => {
                    const isFirst = index === 0;
                    const isLast = index === Object.keys(alert.steps!).length - 1;
                    const isSingle = Object.keys(alert.steps!).length === 1;
                    const permissions = (action as string).split(',').map(p => p.trim()).filter(p => p);
                    
                    return (
                      <div key={step} className="relative flex items-start gap-3">
                        <div
                          className={`shrink-0 w-5 h-5 mt-2.5 rounded-full border-2 flex items-center justify-center z-10 ${
                            isSingle
                              ? 'bg-blue-500 border-blue-500'
                              : isFirst
                                ? 'bg-primary border-primary'
                                : isLast
                                  ? 'bg-destructive border-destructive'
                                  : 'bg-background border-border'
                          }`}
                        >
                          <span
                            className={`text-[10px] font-bold ${
                              isSingle || isFirst || isLast ? 'text-white' : 'text-foreground'
                            }`}
                          >
                            {step}
                          </span>
                        </div>
                        <div className="flex-1 bg-muted/50 rounded-lg p-2.5">
                          <div className="flex flex-wrap items-center gap-2">
                            {permissions.map((permission, pIndex) => (
                              <code 
                                key={pIndex} 
                                className="text-xs font-mono text-foreground bg-background/80 px-2.5 py-1 rounded border border-border"
                              >
                                {permission}
                              </code>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Analysis Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Analysis
            </h3>

            <div className="space-y-3">
              {isCreateMode ? (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    Why this matters
                    <Edit2 className="h-3 w-3 text-muted-foreground" />
                  </h4>
                  <Textarea
                    value={editWhy}
                    onChange={(e) => setEditWhy(e.target.value)}
                    placeholder="Explain why this alert matters..."
                    className="min-h-[60px] ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              ) : alert.info.why && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    Why this matters
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {alert.info.why}
                  </p>
                </div>
              )}

              {isCreateMode ? (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    What happened
                    <Edit2 className="h-3 w-3 text-muted-foreground" />
                  </h4>
                  <Textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Describe what this alert detects..."
                    className="min-h-[60px] ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              ) : alert.info.what && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    What happened
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {alert.info.what}
                  </p>
                </div>
              )}

              {isCreateMode ? (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    Recommended actions
                    <Edit2 className="h-3 w-3 text-muted-foreground" />
                  </h4>
                  <Textarea
                    value={editHow}
                    onChange={(e) => setEditHow(e.target.value)}
                    placeholder="Recommend actions to take..."
                    className="min-h-[60px] ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              ) : alert.info.how && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    Recommended actions
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {alert.info.how}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* MITRE ATT&CK */}
          {alert.mitre.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  MITRE ATT&CK Techniques
                </h3>
                <div className="space-y-2">
                  {alert.mitre.map((technique, index) => (
                    <div key={index} className="flex items-start gap-3 bg-muted/30 rounded-lg p-3">
                      <Badge variant="outline" className="font-mono text-xs shrink-0">
                        {technique.code} • {technique.key}
                      </Badge>
                      {technique.explanation && (
                        <span className="text-sm text-muted-foreground">
                          {technique.explanation}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Selection Sections - Each in its own row */}
          <div className="space-y-6">
            {/* Time Window - First */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Time Window</h3>
              </div>
              
              <TimeWindowSelector
                selectedWindows={selectedTimeWindows}
                onWindowsChange={handleTimeWindowsChange}
              />
            </div>

            {/* Users - Second */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Users</h3>
              </div>

              {isLoadingItems ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : selectableItems?.users && selectableItems.users.length > 0 ? (
                <div className="rounded-md border p-3 max-h-[320px] flex flex-col gap-2">
                  {/* Selected Users */}
                  {selectedUsers.filter(u => selectableItems.users?.includes(u) || !selectableItems.users?.includes(u)).length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">Selected Users</label>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedUsers.map((user) => (
                          <Badge
                            key={user}
                            variant="secondary"
                            className="gap-1.5 pr-1 pl-2 py-1 bg-primary/10 text-primary border border-primary/20 text-xs"
                          >
                            {user}
                            <button
                              onClick={() => handleUserToggle(user)}
                              className="ml-1 p-0.5 rounded-full hover:bg-primary/20 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users..."
                    className="h-9"
                  />
                  <div className="rounded-lg border border-border bg-muted/20 p-2">
                    <div className="max-h-[200px] overflow-y-auto scrollbar-thin space-y-1">
                      {selectableItems.users
                        .filter((u) => u.toLowerCase().includes(userSearch.trim().toLowerCase()))
                        .map((user) => (
                          <label
                            key={user}
                            className={`flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors ${
                              selectedUsers.includes(user) ? 'bg-primary/10 border border-primary/30' : ''
                            }`}
                          >
                            <Checkbox
                              checked={selectedUsers.includes(user)}
                              onCheckedChange={() => handleUserToggle(user)}
                            />
                            <span className="text-sm" title={user}>
                              {user}
                            </span>
                          </label>
                        ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Service Accounts / IAM Roles - Third */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">
                  {selectedCloud === 'AWS' ? 'IAM Roles' : 'Service Accounts'}
                </h3>
              </div>

              {isLoadingItems ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (() => {
                const principals =
                  selectedCloud === 'AWS'
                    ? (selectableItems?.awsIamRole ?? [])
                    : (selectableItems?.serviceAccounts ?? selectableItems?.serviceAccount ?? []);
                // Filter selected users that are principals
                const selectedPrincipals = selectedUsers.filter(u => principals.includes(u) || !selectableItems?.users?.includes(u));
                return principals.length > 0 ? (
                  <div className="rounded-md border p-3 max-h-[320px] flex flex-col gap-2">
                    {/* Selected Service Accounts / IAM Roles */}
                    {selectedPrincipals.filter(p => principals.includes(p)).length > 0 && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">
                          Selected {selectedCloud === 'AWS' ? 'IAM Roles' : 'Service Accounts'}
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedPrincipals.filter(p => principals.includes(p)).map((principal) => (
                            <Badge
                              key={principal}
                              variant="secondary"
                              className="gap-1.5 pr-1 pl-2 py-1 bg-primary/10 text-primary border border-primary/20 text-xs"
                            >
                              {principal}
                              <button
                                onClick={() => handleUserToggle(principal)}
                                className="ml-1 p-0.5 rounded-full hover:bg-primary/20 transition-colors"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <Input
                      value={principalSearch}
                      onChange={(e) => setPrincipalSearch(e.target.value)}
                      placeholder={selectedCloud === 'AWS' ? 'Search roles...' : 'Search service accounts...'}
                      className="h-9"
                    />
                    <div className="rounded-lg border border-border bg-muted/20 p-2">
                      <div className="max-h-[200px] overflow-y-auto scrollbar-thin space-y-1">
                        {principals
                          .filter((p) => p.toLowerCase().includes(principalSearch.trim().toLowerCase()))
                          .map((principal) => (
                            <label
                              key={principal}
                              className={`flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors ${
                                selectedUsers.includes(principal) ? 'bg-primary/10 border border-primary/30' : ''
                              }`}
                            >
                              <Checkbox
                                checked={selectedUsers.includes(principal)}
                                onCheckedChange={() => handleUserToggle(principal)}
                              />
                              <span className="text-sm" title={principal}>
                                {principal}
                              </span>
                            </label>
                          ))}
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Resources - Fourth */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Server className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Resources</h3>
              </div>

              {isLoadingItems ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : selectableItems?.resources && Object.keys(selectableItems.resources).length > 0 ? (
                <div className="rounded-md border p-3 max-h-[320px] flex flex-col gap-2">
                  {/* Selected Resources */}
                  {selectedResources.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">Selected Resources</label>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedResources.map((resource) => (
                          <Badge
                            key={resource}
                            variant="secondary"
                            className="gap-1.5 pr-1 pl-2 py-1 bg-primary/10 text-primary border border-primary/20 text-xs"
                          >
                            {resource}
                            <button
                              onClick={() => handleResourceToggle(resource)}
                              className="ml-1 p-0.5 rounded-full hover:bg-primary/20 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <Input
                    value={resourceSearch}
                    onChange={(e) => setResourceSearch(e.target.value)}
                    placeholder="Search resources..."
                    className="h-9"
                  />

                  <div className="rounded-lg border border-border bg-muted/20 p-2">
                    <div className="max-h-[240px] overflow-y-auto scrollbar-thin">
                      <Accordion type="multiple" defaultValue={[]} className="w-full space-y-1">
                        {(selectedCloud === 'AWS'
                          ? ['ec2', 'rds', 's3']
                          : ['virtual-machine', 'database', 'storage-bucket']
                        ).map((type) => {
                          const resources = selectableItems.resources?.[type] ?? [];
                          const filtered = resources.filter((r) =>
                            r.toLowerCase().includes(resourceSearch.trim().toLowerCase())
                          );
                          const selectedCount = resources.filter((r) => selectedResources.includes(r)).length;

                          if (resources.length === 0) return null;

                          return (
                            <AccordionItem key={type} value={type} className="border rounded-lg px-3 bg-background">
                              <AccordionTrigger className="text-sm py-2 hover:no-underline">
                                <span className="flex items-center gap-2">
                                  <span>{getResourceTypeLabel(type)}</span>
                                  {selectedCount > 0 && (
                                    <Badge variant="secondary" className="text-[10px] h-5">
                                      {selectedCount}
                                    </Badge>
                                  )}
                                </span>
                              </AccordionTrigger>
                              <AccordionContent>
                                {filtered.length === 0 ? (
                                  <p className="text-sm text-muted-foreground py-2">No items</p>
                                ) : (
                                  <div className="rounded-lg border border-border bg-muted/20 p-2 mt-1">
                                    <div className="max-h-[120px] overflow-y-auto scrollbar-thin space-y-1">
                                      {filtered.map((resource) => (
                                        <label
                                          key={resource}
                                          className={`flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors ${
                                            selectedResources.includes(resource)
                                              ? 'bg-primary/10 border border-primary/30'
                                              : ''
                                          }`}
                                        >
                                          <Checkbox
                                            checked={selectedResources.includes(resource)}
                                            onCheckedChange={() => handleResourceToggle(resource)}
                                          />
                                          <span className="text-sm" title={resource}>
                                            {resource}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer with Save Button */}
        <div className="border-t pt-4 flex items-center justify-between gap-3">
          <div className="flex-1">
            {onNavigateToPolicy && (
              <Button
                variant="outline"
                onClick={() => onNavigateToPolicy(alert)}
                className="rounded-2xl border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
              >
                Create Policy
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          {isCreateMode ? (
            <Button
              onClick={handleCreateModeUpdate}
              disabled={!hasChanges}
              className="gap-2"
            >
              <Check className="h-4 w-4" />
              Apply Changes
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
